/**
 * components/FilterPills.jsx — v2.87 Phase 1 (Filter Rebuild)
 * ----------------------------------------------------------
 * Premium 2-row sticky filter strip. Replaces the v2.71.59 single-row
 * pill bar AND consolidates the hero intent toggle into the filter area
 * (so the user can change Sale/Rent/Lease right next to the results).
 *
 *   Row 1 (intent + reset):
 *     [All] [Sale] [Rent] [Lease] ............................. [↺ Reset]
 *
 *   Row 2 (filters):
 *     [Budget ▾] [Property type ▾] [BHK ▾] [Main area ▾]
 *     [Sub area ▾ (when main set)] [Furnishing ▾] [Kab milega? ▾]
 *     [Sort ▾] [☑ Verified] [⋯ More filters]
 *
 * Pill detail surface picks itself based on viewport:
 *   • Desktop ≥ md → anchored dropdown popover (w-[320px]).
 *   • Mobile  < md → Radix bottom <Sheet>.
 *
 * The pill row is designed to live inside a sticky wrapper:
 *   <div className="sticky top-0 z-30 bg-white/90 backdrop-blur">
 *     <FilterPills ... />
 *   </div>
 *
 * URL / state contract — UNCHANGED for back-compat:
 *   • All filter writes go through setFilters() (parent owns the state).
 *   • Dashboard's filterUrl.js mirrors any non-default value as a URL
 *     param; new filters (furnishing / possession_status / sort /
 *     verified / min_size / max_size) flow through automatically.
 *   • "More filters" sheet still hosts the legacy <FilterPanel> for parity
 *     with custom CMS filters.
 */

import React, { useMemo, useRef, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import {
  ChevronDown, X, Sliders, ShieldCheck, ArrowUpDown, Home, KeyRound, Briefcase, Check,
} from "lucide-react";
import FilterPanel from "../pages/FilterPanel";
import { scrollToResults } from "../lib/scrollToResults";
// v2.87 Phase 1 — Static import of ConfigContext for SubAreaPicker.
import { useConfig } from "../lib/ConfigContext";

// ─── intent vocabulary ─────────────────────────────────────────────
const INTENT_OPTIONS = [
  { key: "",      label: "All",   sub: "All listings" },
  { key: "sale",  label: "Sale",  sub: "Buy a property",     Icon: Home },
  { key: "rent",  label: "Rent",  sub: "Tenant rental",      Icon: KeyRound },
  { key: "lease", label: "Lease", sub: "Commercial lease",   Icon: Briefcase },
];

// ─── intent-aware budget vocabulary ────────────────────────────────
// Mirrors propertyTypeMatrix-style intent awareness — Rent shows monthly
// rupee buckets, Lease shows lease-amount buckets, Sale shows crore-scale
// buckets. Replaces the v2.71.59 sale-only hardcoded BUDGET_PRESETS.
const INTENT_BUDGET_PRESETS = {
  sale: [
    { label: "Up to ₹50 L",     range: [0, 5000000] },
    { label: "₹50 L – ₹1 Cr",   range: [5000000, 10000000] },
    { label: "₹1 – 2 Cr",       range: [10000000, 20000000] },
    { label: "₹2 – 5 Cr",       range: [20000000, 50000000] },
    { label: "₹5 Cr+",          range: [50000000, 999999999] },
  ],
  rent: [
    { label: "Up to ₹10 k/mo",  range: [0, 10000] },
    { label: "₹10 – 20 k",      range: [10000, 20000] },
    { label: "₹20 – 30 k",      range: [20000, 30000] },
    { label: "₹30 – 50 k",      range: [30000, 50000] },
    { label: "₹50 k+",          range: [50000, 999999999] },
  ],
  lease: [
    { label: "Up to ₹50 k",     range: [0, 50000] },
    { label: "₹50 k – 1 L",     range: [50000, 100000] },
    { label: "₹1 – 5 L",        range: [100000, 500000] },
    { label: "₹5 – 50 L",       range: [500000, 5000000] },
    { label: "₹50 L+",          range: [5000000, 999999999] },
  ],
};
const INTENT_LABELS = {
  sale:  "Sale budget",
  rent:  "Monthly rent",
  lease: "Lease amount",
  "":    "Budget",
};

// BHK list now derived from /api/filters via configFilters → single source.
// FALLBACK shown only when the admin config hasn't loaded yet.
const BHK_FALLBACK = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK"];

const FURNISHING_OPTIONS = ["Unfurnished", "Semi-Furnished", "Fully Furnished"];

const POSSESSION_OPTIONS = [
  { value: "ready_to_move",       label: "Ready to Move" },
  { value: "under_construction",  label: "Under Construction" },
  { value: "new_launch",          label: "New Launch" },
];

const SORT_OPTIONS = [
  { value: "created_at:desc",       label: "Newest first" },
  { value: "price:asc",             label: "Price: Low → High" },
  { value: "price:desc",            label: "Price: High → Low" },
  { value: "distance_metro_m:asc",  label: "Closest to Metro" },
];
const DEFAULT_SORT = "created_at:desc";

// ─── helpers ────────────────────────────────────────────────────────
const fmtINR = (n) => {
  const num = Number(n) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 1)} Cr`;
  if (num >= 100000)   return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)} L`;
  if (num >= 1000)     return `₹${(num / 1000).toFixed(0)}K`;
  return `₹${num}`;
};

// v2.87 Phase 3a — Multi-select helpers. property_type / bhk / furnishing
// are stored in filters as comma-separated strings ("Apartment,House").
// Backend accepts that natively (=$in over the union). These helpers let
// the UI work with arrays without changing the state shape.
const csvToArr = (csv) => String(csv || "").split(",").map((x) => x.trim()).filter(Boolean);
const arrToCsv = (arr) => (arr || []).filter(Boolean).join(",");
const csvLabel = (csv, fallback) => {
  const arr = csvToArr(csv);
  if (arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  return `${arr[0]} +${arr.length - 1}`;
};

const presetForRange = (range, intent) => {
  if (!range) return null;
  const list = INTENT_BUDGET_PRESETS[intent] || INTENT_BUDGET_PRESETS.sale;
  if (range[0] === 0 && range[1] >= 999999999) return null;
  return list.find((p) => p.range[0] === range[0] && p.range[1] === range[1]) || null;
};

const isDefaultRange = (range, defaults) => {
  if (!range) return true;
  if (defaults && defaults[0] === range[0] && defaults[1] === range[1]) return true;
  return range[0] === 0 && (range[1] === 999999999 || range[1] >= 999999999);
};

const intentTone = (key, active) => {
  if (!active) return "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50";
  if (key === "sale")  return "bg-[#1E40AF] text-white border-[#1E40AF] shadow-sm";
  if (key === "rent")  return "bg-[#92400E] text-white border-[#92400E] shadow-sm";
  if (key === "lease") return "bg-[#5B21B6] text-white border-[#5B21B6] shadow-sm";
  return "bg-[#09090B] text-white border-[#09090B] shadow-sm";
};

// ─── main component ────────────────────────────────────────────────
export default function FilterPills({
  filters,
  setFilters,
  onReset,
  configFilters = [],
  defaults = {},
  testidPrefix = "fp",
}) {
  const [activePop, setActivePop] = useState(null);  // 'budget' | 'type' | 'bhk' | 'main' | 'sub' | 'furn' | 'poss' | 'sort' | null
  const [moreOpen, setMoreOpen] = useState(false);
  const rootRef = useRef(null);

  const intent = filters.listing_intent || "";

  // Close popover on outside click (desktop only). Mobile uses Radix Sheet
  // dismissal — outside-click handler is skipped to avoid the portal trap
  // (see v2.71.71 fix).
  useEffect(() => {
    if (!activePop) return;
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setActivePop(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [activePop]);

  // Resolve dropdown option lists from /api/filters via configFilters prop.
  const typeOptions = useMemo(() => {
    const f = (configFilters || []).find((x) => x.key === "property_type");
    return (f?.options || []).filter(Boolean);
  }, [configFilters]);

  const bhkOptions = useMemo(() => {
    const f = (configFilters || []).find((x) => x.key === "bhk");
    const opts = (f?.options || []).filter(Boolean);
    return opts.length ? opts : BHK_FALLBACK;
  }, [configFilters]);

  // v2.87 — Sub Area options scoped by selected Main Area. We need a
  // locations[] lookup which lives on /api/config.locations. Resolve via
  // configFilters' parent (we don't have direct access here, so we pull
  // from window.__houseEntryConfigLocations cache set by ConfigContext if
  // available; otherwise we fall back to a flat list).
  // For now, sub-area picker stays inside "More filters" sheet for now —
  // BUT we surface Main Area as a primary pill.
  const mainAreaOptions = useMemo(() => {
    const f = (configFilters || []).find((x) => x.key === "main_area");
    return (f?.options || []).filter(Boolean);
  }, [configFilters]);

  // ─── derived labels ────────────────────────────────────────────
  const budgetPreset = presetForRange(filters.priceRange, intent || "sale");
  const budgetLabel = budgetPreset
    ? budgetPreset.label
    : !isDefaultRange(filters.priceRange, defaults.priceRange)
      ? (filters.priceRange[0] === 0
          ? `Up to ${fmtINR(filters.priceRange[1])}`
          : `${fmtINR(filters.priceRange[0])} – ${fmtINR(filters.priceRange[1])}`)
      : "";

  const sortLabel = SORT_OPTIONS.find((o) => o.value === (filters.sort || DEFAULT_SORT))?.label
    || "Newest first";
  const sortActive = (filters.sort || DEFAULT_SORT) !== DEFAULT_SORT;

  const verifiedOn = !!filters.verified;

  // ─── patch helpers ─────────────────────────────────────────────
  const setPatch = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const handleIntentChange = (key) => {
    setFilters((prev) => ({ ...prev, listing_intent: key }));
    setActivePop(null);
    // v2.87 — smooth-scroll to listings on intent change so the result
    // grid actually updates in view instead of staying behind the hero.
    requestAnimationFrame(() => scrollToResults());
  };

  // Count active filters (excluding intent — intent is its own row).
  const activeCount = (() => {
    let n = 0;
    if (filters.property_type)                                     n++;
    if (filters.bhk)                                               n++;
    if (filters.main_area)                                         n++;
    if (filters.sub_area)                                          n++;
    if (filters.furnishing)                                        n++;
    if (filters.possession_status)                                 n++;
    if (filters.verified)                                          n++;
    if (filters.tag)                                               n++;
    if (filters.posted_by)                                         n++;
    if (filters.listing_type)                                      n++;
    if (sortActive)                                                n++;
    if (budgetLabel)                                               n++;
    if (filters.maxMetro != null && filters.maxMetro !== (defaults.maxMetro ?? 3000)) n++;
    if (filters.min_size || filters.max_size)                      n++;
    return n;
  })();

  return (
    <div
      ref={rootRef}
      className="relative w-full"
      data-testid={`${testidPrefix}-root`}
    >
      {/* ===== Row 1 — Intent chips (sticky, primary) ===== */}
      <div
        className="flex items-center gap-2 overflow-x-auto md:overflow-visible no-scrollbar pt-2 pb-1"
        data-testid={`${testidPrefix}-intent-row`}
      >
        {INTENT_OPTIONS.map(({ key, label, Icon }) => {
          const active = (intent || "") === key;
          return (
            <button
              key={key || "all"}
              type="button"
              onClick={() => handleIntentChange(key)}
              data-testid={`${testidPrefix}-intent-${key || "all"}`}
              aria-pressed={active}
              className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full border text-[12.5px] font-semibold transition-all duration-200
                          ${intentTone(key, active)}`}
            >
              {Icon && <Icon size={12} strokeWidth={2.4} />}
              {label}
            </button>
          );
        })}
        {/* Reset link — visible only when at least one filter is non-default */}
        {(activeCount > 0 || intent) && (
          <button
            type="button"
            onClick={() => { onReset?.(); setActivePop(null); scrollToResults(); }}
            data-testid={`${testidPrefix}-reset`}
            className="ml-auto shrink-0 inline-flex items-center gap-1 h-9 px-3 text-[12px] text-slate-500 hover:text-slate-900 transition-colors"
            aria-label="Reset filters"
          >
            <X size={12} /> Reset all
          </button>
        )}
      </div>

      {/* ===== Row 2 — Filter pills ===== */}
      <div
        className="flex items-center gap-2 overflow-x-auto md:overflow-visible no-scrollbar pb-2"
        data-testid={`${testidPrefix}-row`}
      >
        <Pill
          label={INTENT_LABELS[intent || ""]}
          value={budgetLabel}
          active={activePop === "budget"}
          onClick={() => setActivePop((p) => (p === "budget" ? null : "budget"))}
          testid={`${testidPrefix}-budget`}
        />
        <Pill
          label="Property type"
          value={csvLabel(filters.property_type)}
          active={activePop === "type"}
          onClick={() => setActivePop((p) => (p === "type" ? null : "type"))}
          testid={`${testidPrefix}-type`}
        />
        <Pill
          label="BHK"
          value={csvLabel(filters.bhk)}
          active={activePop === "bhk"}
          onClick={() => setActivePop((p) => (p === "bhk" ? null : "bhk"))}
          testid={`${testidPrefix}-bhk`}
        />
        <Pill
          label="Main area"
          value={csvLabel(filters.main_area)}
          active={activePop === "main"}
          onClick={() => setActivePop((p) => (p === "main" ? null : "main"))}
          testid={`${testidPrefix}-main`}
        />
        {/* Sub Area surfaces inline once EXACTLY ONE Main Area is picked.
            Multi-pick main areas → sub_area is ambiguous (cross-main scope)
            so we hide the pill rather than offer a confusing UX. */}
        {csvToArr(filters.main_area).length === 1 && (
          <Pill
            label="Sub area"
            value={filters.sub_area || ""}
            active={activePop === "sub"}
            onClick={() => setActivePop((p) => (p === "sub" ? null : "sub"))}
            testid={`${testidPrefix}-sub`}
          />
        )}
        <Pill
          label="Furnishing"
          value={csvLabel(filters.furnishing)}
          active={activePop === "furn"}
          onClick={() => setActivePop((p) => (p === "furn" ? null : "furn"))}
          testid={`${testidPrefix}-furn`}
        />
        {/* Possession only meaningful for Sale; render for all intents but
            content is filterable. We always show the pill since user spec
            asked for it as a primary surface. */}
        <Pill
          label="Kab milega?"
          value={POSSESSION_OPTIONS.find((o) => o.value === filters.possession_status)?.label || ""}
          active={activePop === "poss"}
          onClick={() => setActivePop((p) => (p === "poss" ? null : "poss"))}
          testid={`${testidPrefix}-poss`}
        />
        <Pill
          label="Sort"
          value={sortActive ? sortLabel : ""}
          icon={<ArrowUpDown size={11} />}
          active={activePop === "sort"}
          onClick={() => setActivePop((p) => (p === "sort" ? null : "sort"))}
          testid={`${testidPrefix}-sort`}
        />
        {/* v2.87 — Verified is now its OWN boolean key (`verified=true`).
            Decoupled from `tag` so it no longer fights Tags selections. */}
        <button
          type="button"
          onClick={() => setPatch({ verified: verifiedOn ? false : true })}
          aria-pressed={verifiedOn}
          data-testid={`${testidPrefix}-verified`}
          className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full border text-[12.5px] font-semibold transition-all duration-200
                      ${verifiedOn
                        ? "bg-[#15803D] text-white border-[#15803D] shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#15803D]/40 hover:bg-slate-50"}`}
        >
          <ShieldCheck size={13} /> Verified
        </button>
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          data-testid={`${testidPrefix}-more`}
          className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-slate-200 bg-white text-slate-700 text-[12.5px] font-semibold hover:border-[#0055FF]/40 hover:bg-slate-50 transition-all"
        >
          <Sliders size={13} /> More filters
          {activeCount > 6 && (
            <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-[#0055FF] text-white text-[9.5px] font-bold">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* ===== Pill detail surfaces ===== */}
      {activePop === "budget" && (
        <PillDetail title={INTENT_LABELS[intent || ""]} anchor="left" testid={`${testidPrefix}-pop-budget`} onClose={() => setActivePop(null)}>
          <BudgetEditor
            intent={intent || "sale"}
            value={filters.priceRange || [0, 999999999]}
            onApply={(range) => { setPatch({ priceRange: range }); setActivePop(null); scrollToResults(); }}
            testidPrefix={`${testidPrefix}-budget`}
          />
        </PillDetail>
      )}
      {activePop === "type" && (
        <PillDetail title="Property type" anchor="left" testid={`${testidPrefix}-pop-type`} onClose={() => setActivePop(null)}>
          <MultiOptionGrid
            options={typeOptions}
            value={filters.property_type}
            onApply={(csv) => { setPatch({ property_type: csv }); setActivePop(null); scrollToResults(); }}
            testidPrefix={`${testidPrefix}-type`}
            cols={2}
            emptyText="No property types configured"
          />
        </PillDetail>
      )}
      {activePop === "bhk" && (
        <PillDetail title="BHK" anchor="left" testid={`${testidPrefix}-pop-bhk`} onClose={() => setActivePop(null)}>
          <MultiOptionGrid
            options={bhkOptions}
            value={filters.bhk}
            onApply={(csv) => { setPatch({ bhk: csv }); setActivePop(null); scrollToResults(); }}
            testidPrefix={`${testidPrefix}-bhk`}
            cols={3}
          />
        </PillDetail>
      )}
      {activePop === "main" && (
        <PillDetail title="Main area" anchor="left" testid={`${testidPrefix}-pop-main`} onClose={() => setActivePop(null)}>
          <MultiOptionGrid
            options={mainAreaOptions}
            value={filters.main_area}
            onApply={(csv) => {
              // Reset sub_area whenever the main_area selection changes —
              // sub_area is scoped under a single main area.
              setPatch({ main_area: csv, sub_area: "" });
              setActivePop(null);
              scrollToResults();
            }}
            testidPrefix={`${testidPrefix}-main`}
            cols={2}
            emptyText="No areas configured"
          />
        </PillDetail>
      )}
      {activePop === "sub" && csvToArr(filters.main_area).length === 1 && (
        <PillDetail title="Sub area" anchor="left" testid={`${testidPrefix}-pop-sub`} onClose={() => setActivePop(null)}>
          <SubAreaPicker
            mainArea={csvToArr(filters.main_area)[0]}
            value={filters.sub_area}
            onPick={(v) => { setPatch({ sub_area: v === filters.sub_area ? "" : v }); setActivePop(null); scrollToResults(); }}
            testidPrefix={`${testidPrefix}-sub`}
          />
        </PillDetail>
      )}
      {activePop === "furn" && (
        <PillDetail title="Furnishing" anchor="left" testid={`${testidPrefix}-pop-furn`} onClose={() => setActivePop(null)}>
          <MultiOptionGrid
            options={FURNISHING_OPTIONS}
            value={filters.furnishing}
            onApply={(csv) => { setPatch({ furnishing: csv }); setActivePop(null); scrollToResults(); }}
            testidPrefix={`${testidPrefix}-furn`}
            cols={1}
          />
        </PillDetail>
      )}
      {activePop === "poss" && (
        <PillDetail title="Kab milega? (Possession)" anchor="left" testid={`${testidPrefix}-pop-poss`} onClose={() => setActivePop(null)}>
          <ValueLabelGrid
            options={POSSESSION_OPTIONS}
            value={filters.possession_status}
            onPick={(v) => { setPatch({ possession_status: v === filters.possession_status ? "" : v }); setActivePop(null); scrollToResults(); }}
            testidPrefix={`${testidPrefix}-poss`}
          />
        </PillDetail>
      )}
      {activePop === "sort" && (
        <PillDetail title="Sort by" anchor="left" testid={`${testidPrefix}-pop-sort`} onClose={() => setActivePop(null)}>
          <ValueLabelGrid
            options={SORT_OPTIONS}
            value={filters.sort || DEFAULT_SORT}
            onPick={(v) => {
              // Clearing back to default is "unset" — keep URL clean.
              setPatch({ sort: v === DEFAULT_SORT ? "" : v });
              setActivePop(null);
              scrollToResults();
            }}
            testidPrefix={`${testidPrefix}-sort`}
          />
        </PillDetail>
      )}

      {/* ===== "More filters" bottom sheet (hosts the legacy FilterPanel) ===== */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="p-0 h-[90dvh] sm:max-w-2xl sm:mx-auto sm:rounded-t-2xl flex flex-col"
          data-testid={`${testidPrefix}-more-sheet`}
        >
          <SheetHeader className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-[15px] font-semibold">All filters</SheetTitle>
              <button
                onClick={() => setMoreOpen(false)}
                aria-label="Close"
                className="text-slate-500 hover:text-slate-900"
                data-testid={`${testidPrefix}-more-close`}
              >
                <X size={18} />
              </button>
            </div>
            <SheetDescription className="sr-only">All available filters for the listing search.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
            <FilterPanel filters={filters} setFilters={setFilters} configFilters={configFilters} onReset={onReset} />
          </div>
          <div className="border-t bg-white p-3 flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={() => { onReset?.(); setMoreOpen(false); scrollToResults(); }} className="flex-1 h-10" data-testid={`${testidPrefix}-more-clear`}>
              Clear all
            </Button>
            <Button onClick={() => { setMoreOpen(false); scrollToResults(); }} className="flex-1 h-10 bg-[#0055FF] hover:bg-[#0044CC]" data-testid={`${testidPrefix}-more-apply`}>
              Apply
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── building blocks ────────────────────────────────────────────────
function Pill({ label, value, active, onClick, testid, icon = null }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-expanded={active}
      className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full border text-[12.5px] font-semibold transition-all duration-200
                  ${active
                    ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                    : value
                      ? "bg-[#0055FF] text-white border-[#0055FF] shadow-sm"
                      : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
    >
      {icon}
      <span>{value || label}</span>
      <ChevronDown size={12} className={`transition-transform duration-200 ${active ? "rotate-180" : ""}`} />
    </button>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

function PillDetail({ children, testid, anchor, title, onClose }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(true);
  const closeTimerRef = useRef(null);
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);
  const closeSelfAndParent = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => onClose?.(), 220);
  };
  const anchorClass = anchor === "right" ? "right-0"
    : anchor === "center" ? "left-1/2 -translate-x-1/2"
    : "left-0";
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(v) => { if (!v) closeSelfAndParent(); }}>
        <SheetContent side="bottom" className="p-0 max-h-[80dvh] rounded-t-2xl flex flex-col" data-testid={testid}>
          <SheetHeader className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-[14.5px] font-semibold">{title}</SheetTitle>
              <button type="button" onClick={closeSelfAndParent} aria-label="Close" className="text-slate-500 hover:text-slate-900">
                <X size={18} />
              </button>
            </div>
            <SheetDescription className="sr-only">Pick a {title} option.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <div
      data-testid={testid}
      className={`absolute top-full mt-2 ${anchorClass} z-50
                  bg-white rounded-2xl border border-slate-100 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.18),0_4px_10px_-2px_rgba(15,23,42,0.08)]
                  overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 w-[320px]`}
    >
      {children}
    </div>
  );
}

function BudgetEditor({ intent, value, onApply, testidPrefix }) {
  const presets = INTENT_BUDGET_PRESETS[intent] || INTENT_BUDGET_PRESETS.sale;
  const [minVal, setMinVal] = useState(value[0] || "");
  const [maxVal, setMaxVal] = useState(value[1] >= 999999999 ? "" : value[1] || "");
  return (
    <div className="p-4 space-y-3">
      <div className="space-y-1.5">
        {presets.map((p) => (
          <button
            key={p.label}
            onClick={() => onApply(p.range)}
            className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50 text-[13px] font-semibold transition-all"
            data-testid={`${testidPrefix}-preset-${p.label.replace(/[\s+/]+/g, "-")}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Or custom</div>
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" value={minVal} onChange={(e) => setMinVal(e.target.value)} placeholder="Min" className="h-9 text-[13px]" data-testid={`${testidPrefix}-min`} />
          <Input type="number" value={maxVal} onChange={(e) => setMaxVal(e.target.value)} placeholder="Max" className="h-9 text-[13px]" data-testid={`${testidPrefix}-max`} />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onApply([Number(minVal) || 0, Number(maxVal) || 999999999])}
          disabled={!minVal && !maxVal}
          className="w-full mt-2 h-9"
          data-testid={`${testidPrefix}-apply-custom`}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

function OptionGrid({ options, value, onPick, testidPrefix, cols = 2, emptyText = "Nothing configured" }) {
  const gridClass = cols === 1 ? "grid-cols-1" : cols === 3 ? "grid-cols-3" : "grid-cols-2";
  return (
    <div className={`p-3 grid ${gridClass} gap-2 max-h-[60vh] overflow-y-auto`}>
      {options.length === 0 ? (
        <div className={`col-span-${cols} text-center text-[12px] text-slate-500 py-6`}>{emptyText}</div>
      ) : options.map((t) => {
        const active = value === t;
        return (
          <button
            key={t}
            onClick={() => onPick(t)}
            className={`text-[12.5px] font-semibold py-2.5 rounded-xl border transition-all inline-flex items-center justify-center gap-1.5
                        ${active
                          ? "bg-[#0055FF] text-white border-[#0055FF]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
            data-testid={`${testidPrefix}-opt-${String(t).replace(/[\s/]+/g, "-")}`}
          >
            {active && <Check size={12} />}
            {t}
          </button>
        );
      })}
    </div>
  );
}

/**
 * MultiOptionGrid — multi-select staged picker.
 * Selection is buffered locally; commit happens on Apply (or Reset).
 * Same-group multi-pick → backend `$in` (OR). Different groups → AND
 * (handled by composing different filter keys at the parent level).
 */
function MultiOptionGrid({ options, value, onApply, testidPrefix, cols = 2, emptyText = "Nothing configured" }) {
  const initial = csvToArr(value);
  const [staged, setStaged] = useState(initial);
  // Re-sync staged on incoming value change (e.g. pop reopened with different value).
  useEffect(() => { setStaged(csvToArr(value)); /* eslint-disable-line */ }, [value]);
  const gridClass = cols === 1 ? "grid-cols-1" : cols === 3 ? "grid-cols-3" : "grid-cols-2";
  const toggle = (t) => setStaged((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);
  const dirty = staged.join(",") !== initial.join(",");
  return (
    <div className="flex flex-col" data-testid={`${testidPrefix}-multi`}>
      <div className={`p-3 grid ${gridClass} gap-2 overflow-y-auto`} style={{ maxHeight: "min(60vh, 400px)" }}>
        {options.length === 0 ? (
          <div className="col-span-full text-center text-[12px] text-slate-500 py-6">{emptyText}</div>
        ) : options.map((t) => {
          const active = staged.includes(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggle(t)}
              className={`text-[12.5px] font-semibold py-2.5 rounded-xl border transition-all inline-flex items-center justify-center gap-1.5
                          ${active
                            ? "bg-[#0055FF] text-white border-[#0055FF]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
              data-testid={`${testidPrefix}-opt-${String(t).replace(/[\s/]+/g, "-")}`}
              aria-pressed={active}
            >
              {active && <Check size={12} />}
              {t}
            </button>
          );
        })}
      </div>
      <div className="border-t border-slate-100 bg-white px-3 py-2 flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => setStaged([])}
          disabled={staged.length === 0}
          className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40 px-2.5 py-1.5"
          data-testid={`${testidPrefix}-reset`}
        >
          Reset
        </button>
        <Button
          size="sm"
          onClick={() => onApply(arrToCsv(staged))}
          disabled={!dirty}
          className="ml-auto h-8 px-4 bg-[#0055FF] hover:bg-[#0044CC]"
          data-testid={`${testidPrefix}-apply`}
        >
          Apply{staged.length > 0 && ` (${staged.length})`}
        </Button>
      </div>
    </div>
  );
}

function ValueLabelGrid({ options, value, onPick, testidPrefix }) {
  return (
    <div className="p-2 max-h-[60vh] overflow-y-auto">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            onClick={() => onPick(o.value)}
            className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all text-[13px] font-semibold mb-1.5 inline-flex items-center justify-between
                        ${active
                          ? "bg-[#0055FF] text-white border-[#0055FF]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
            data-testid={`${testidPrefix}-opt-${o.value.replace(/[\s:/+]+/g, "-")}`}
          >
            <span>{o.label}</span>
            {active && <Check size={13} />}
          </button>
        );
      })}
    </div>
  );
}

function SubAreaPicker({ mainArea, value, onPick, testidPrefix }) {
  // ConfigContext is the canonical source for locations[].sub_areas.
  const { locations = [] } = useConfig() || { locations: [] };
  const subs = (locations.find((m) => m.name === mainArea)?.sub_areas || []).map((s) => s.name).filter(Boolean);
  return (
    <OptionGrid
      options={subs}
      value={value}
      onPick={onPick}
      testidPrefix={testidPrefix}
      cols={2}
      emptyText="No sub areas in this main area"
    />
  );
}
